import { useLocation, useParams, useNavigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { useApi } from "@/hooks/useApi";
import { LogOut, Moon, Sun, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const sectionTitles: Record<string, string> = {
  dashboard: "Dashboard",
  profile: "Profile",
  clients: "Partners",
  new: "New Partner",
  chores: "Chores",
  rules: "Rules",
  rewards: "Rewards",
  punishments: "Punishments",
  sessions: "Sessions",
  messages: "Messages",
};

export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, jwt, role } = useAuth();
  const { clientId } = useParams();
  const { toggle, theme } = useTheme();

  // Fetch clients to resolve clientId → name
  const { data: clients } = useApi<Array<{ _key: string; name: string; icon: string; points: number }>>(
    role === "admin" && clientId ? `/api/admin/${jwt}/clients` : null
  );

  const activeClient = clients?.find((c) => c._key === clientId);

  // Build breadcrumb segments
  const getBreadcrumbs = (): Array<{ label: string; isLast: boolean }> => {
    const parts = location.pathname.split("/").filter(Boolean);
    // parts: ["admin"|"client", jwt, ...rest]
    const rest = parts.slice(2); // skip role and jwt

    if (rest.length === 0) return [{ label: "ChoreApp", isLast: true }];

    // Client role: simple title
    if (role === "client") {
      const last = rest[rest.length - 1] ?? "";
      return [{ label: sectionTitles[last] || "ChoreApp", isLast: true }];
    }

    // Admin role
    const crumbs: Array<{ label: string; isLast: boolean }> = [];

    if (rest[0] === "dashboard") {
      return [{ label: "Dashboard", isLast: true }];
    }

    if (rest[0] === "clients") {
      crumbs.push({ label: "Partners", isLast: rest.length === 1 });
      if (rest[1] === "new") {
        crumbs.push({ label: "New Partner", isLast: true });
      }
      return crumbs;
    }

    // clientId path: rest[0] is the clientId
    if (clientId) {
      crumbs.push({ label: "Partners", isLast: false });
      const clientLabel = activeClient ? `${activeClient.icon} ${activeClient.name}` : clientId;
      crumbs.push({ label: clientLabel, isLast: rest.length === 1 });

      if (rest.length > 1) {
        const section = rest[rest.length - 1] ?? "";
        crumbs.push({ label: sectionTitles[section] || section, isLast: true });
      }
      return crumbs;
    }

    // Fallback
    const last = rest[rest.length - 1] ?? "";
    return [{ label: sectionTitles[last] || "ChoreApp", isLast: true }];
  };

  const breadcrumbs = getBreadcrumbs();
  const pageTitle = breadcrumbs[breadcrumbs.length - 1]?.label || "ChoreApp";

  return (
    <header className="h-14 flex items-center justify-between px-3 md:px-4 border-b border-app-border bg-app-surface fixed top-0 left-0 right-0 z-40 md:relative md:z-auto">
      {/* Subtle gradient accent line at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-linear-to-r from-app-primary/40 via-app-primary/20 to-transparent" />

      <div className="flex items-center gap-2 min-w-0">
        <span className="md:hidden text-lg shrink-0">✨</span>

        {/* Mobile: just the title */}
        <h2 className="text-heading font-bold md:hidden truncate">{pageTitle}</h2>

        {/* Desktop: breadcrumbs */}
        <div className="hidden md:flex items-center gap-1 min-w-0">
          {breadcrumbs.map((crumb, i) => (
            <div key={i} className="flex items-center gap-1 min-w-0">
              {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-app-text-muted shrink-0" />}
              <span
                className={cn(
                  "truncate",
                  crumb.isLast
                    ? "text-heading font-bold text-app-text"
                    : "text-body text-app-text-muted"
                )}
              >
                {crumb.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {user && (
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          {role === "admin" ? (
            <button
              onClick={() => navigate(`/admin/${jwt}/profile`)}
              className="text-info text-app-text-muted hidden sm:inline hover:text-app-primary transition-colors cursor-pointer"
              title="Edit profile"
            >
              {user.icon} {user.name}
            </button>
          ) : (
            <span className="text-info text-app-text-muted hidden sm:inline">
              {user.icon} {user.name}
            </span>
          )}
          <button
            onClick={toggle}
            className="p-1.5 rounded-lg text-app-text-muted hover:text-app-primary hover:bg-app-surface-raised transition-colors"
            title="Toggle theme"
          >
            {theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches) ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={logout}
            className="p-1.5 rounded-lg text-app-text-muted hover:text-app-danger hover:bg-app-surface-raised transition-colors"
            title="Log out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      )}
    </header>
  );
}
